import React from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import "./Landing.css";

// Brand mark — the heart-in-bubble from the favicon, inline so it scales cleanly.
export function Mark({ size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 512 512" aria-hidden="true" style={{ verticalAlign: "-0.35em" }}>
      <defs>
        <linearGradient id="lpmk" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FF7A59" /><stop offset="1" stopColor="#FF5C8A" />
        </linearGradient>
      </defs>
      <g fill="url(#lpmk)">
        <rect x="44" y="72" width="424" height="312" rx="84" />
        <polygon points="150,372 138,462 286,382" />
      </g>
      <path fill="#fff" d="M256 336 c-70-44-118-84-118-140 0-40 30-64 62-64 24 0 44 14 56 34 12-20 32-34 56-34 32 0 62 24 62 64 0 56-48 96-118 140z" />
    </svg>
  );
}

// A warm, kind-faced portrait — an original illustration (so it's copyright-free
// and can't break like a hotlinked stock photo). Swap for a real photo later by
// dropping a file in public/ and pointing the photo panel at it.
function Portrait() {
  return (
    <svg viewBox="0 0 200 250" width="100%" height="100%" preserveAspectRatio="xMidYMid slice"
      style={{ display: "block" }} role="img" aria-label="A smiling older woman">
      <defs>
        <linearGradient id="pf-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffe3cd" /><stop offset="1" stopColor="#ffd2dd" />
        </linearGradient>
        <linearGradient id="pf-skin" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f0c4a3" /><stop offset="1" stopColor="#e6b193" />
        </linearGradient>
      </defs>
      <rect width="200" height="250" fill="url(#pf-bg)" />
      {/* shoulders / sweater */}
      <path d="M28 250 C30 202 62 184 100 184 C138 184 170 202 172 250 Z" fill="#6fae9f" />
      <path d="M100 184 c-10 10 -10 18 0 26 c10 -8 10 -16 0 -26 Z" fill="#ffffff" opacity="0.25" />
      {/* neck */}
      <rect x="88" y="150" width="24" height="40" rx="10" fill="url(#pf-skin)" />
      {/* hair behind */}
      <ellipse cx="100" cy="96" rx="60" ry="62" fill="#d3d1d6" />
      {/* ears */}
      <circle cx="52" cy="104" r="9" fill="url(#pf-skin)" />
      <circle cx="148" cy="104" r="9" fill="url(#pf-skin)" />
      {/* face */}
      <ellipse cx="100" cy="100" rx="47" ry="53" fill="url(#pf-skin)" />
      {/* hair over the crown */}
      <path d="M55 92 Q58 50 100 48 Q142 50 145 92 Q140 66 100 66 Q60 66 55 92 Z" fill="#cdccd2" />
      {/* cheeks */}
      <circle cx="72" cy="118" r="11" fill="#f4a0a0" opacity="0.35" />
      <circle cx="128" cy="118" r="11" fill="#f4a0a0" opacity="0.35" />
      {/* eyebrows */}
      <path d="M66 92 Q76 88 86 92" stroke="#bdbcC3" strokeWidth="3" fill="none" strokeLinecap="round" />
      <path d="M114 92 Q124 88 134 92" stroke="#bdbcc3" strokeWidth="3" fill="none" strokeLinecap="round" />
      {/* gentle smiling eyes */}
      <path d="M68 106 Q76 98 84 106" stroke="#5a4636" strokeWidth="3.2" fill="none" strokeLinecap="round" />
      <path d="M116 106 Q124 98 132 106" stroke="#5a4636" strokeWidth="3.2" fill="none" strokeLinecap="round" />
      {/* glasses */}
      <g stroke="#c9a24a" strokeWidth="2.6" fill="none">
        <rect x="60" y="98" width="30" height="22" rx="9" />
        <rect x="110" y="98" width="30" height="22" rx="9" />
        <path d="M90 106 Q100 102 110 106" />
      </g>
      {/* nose */}
      <path d="M100 110 Q104 122 97 124" stroke="#d59f7e" strokeWidth="2.4" fill="none" strokeLinecap="round" />
      {/* warm smile */}
      <path d="M82 134 Q100 150 118 134" stroke="#c66a62" strokeWidth="3.4" fill="none" strokeLinecap="round" />
    </svg>
  );
}

// The demo slideshow shown in the mock's photo panel. resident.jpg already ships;
// drop the rest into client/public/demo/ (any that are missing are simply skipped).
const DEMO_PHOTOS = [
  "/resident.jpg",
  "/demo/waterfall.jpg",
  "/demo/rose.jpg",
  "/demo/sunflower.jpg",
  "/demo/portrait-blue.jpg",
];

// Cross-fading slideshow for the mock's photo panel — it demonstrates the
// product's signature behavior (messages stay put while photos rotate). Preloads
// each source and cycles ONLY the ones that actually load, so a missing file is
// skipped and the illustration behind shows through if none load.
function PhotoCycle({ photos, interval = 3200 }) {
  const [loaded, setLoaded] = React.useState([]);
  const [i, setI] = React.useState(0);
  React.useEffect(() => {
    let alive = true;
    Promise.all(photos.map((src) => new Promise((resolve) => {
      const im = new Image();
      im.onload = () => resolve(src);
      im.onerror = () => resolve(null);
      im.src = src;
    }))).then((r) => { if (alive) setLoaded(r.filter(Boolean)); });
    return () => { alive = false; };
  }, [photos]);
  React.useEffect(() => {
    if (loaded.length <= 1) return undefined;
    const id = setInterval(() => setI((n) => (n + 1) % loaded.length), interval);
    return () => clearInterval(id);
  }, [loaded, interval]);
  return (
    <>
      {loaded.map((src, idx) => (
        <img key={src} src={src} alt="" aria-hidden="true"
          className={"lp-dev-img" + (idx === i ? " on" : "")} />
      ))}
    </>
  );
}

// A CSS mock of the in-room screen: date banner + notes (one a glowing reminder)
// on the left, a photo panel on the right — the real product, at a glance.
export function DeviceMock() {
  return (
    <div className="lp-device" aria-hidden="true">
      <div className="lp-dev-bar">
        <span>Tuesday</span><span>Jul 9</span><span>2:14 PM</span>
      </div>
      <div className="lp-dev-body">
        <div className="lp-dev-notes">
          <div className="lp-note n1">Good morning, Mom! ☀️<br />Thinking of you today.</div>
          <div className="lp-note n2 remind">⏰ Family visit · 3:00<br />Sarah &amp; the kids</div>
          <div className="lp-note n3">Happy anniversary!<br />💙 52 years</div>
        </div>
        <div className="lp-dev-photo">
          <Portrait />
          {/* Real photos cross-fade over the illustration (slideshow); if none of
              the files are present the illustration shows through. */}
          <PhotoCycle photos={DEMO_PHOTOS} />
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const auth = useSelector((state) => state.auth);
  const signedIn = auth && auth.tenantId;
  const startHref = signedIn ? "/home" : "/start";
  const startLabel = signedIn ? "Go to your board" : "Start your 30-day free trial";

  return (
    <div className="lp">
      <div className="lp-wrap">
        <nav className="lp-nav">
          <div className="lp-brand"><Mark /> Mary's Bulletin Board</div>
          <div className="lp-nav-actions">
            {/* First-time visitors: no sign-up/sign-in in the banner — let the
                marketing land first. The trial + sign-in CTAs live lower down
                (pricing + the final section). Returning, signed-in users still
                get a quick link straight to their board. */}
            {signedIn && (
              <Link to="/home" className="lp-btn lp-btn-primary">Go to your board</Link>
            )}
          </div>
        </nav>
      </div>

      <header className="lp-hero">
        <div className="lp-wrap lp-hero-grid">
          <div className="lp-hero-copy">
            <span className="lp-eyebrow">For families with a loved one in care</span>
            <h1>Be there,<br /><span className="grad">even when you can't</span></h1>
            <p className="lp-sub">
              Send a message, a photo, or a visit reminder from your phone — and it lights up
              a screen right in your loved one's room. We ship the screen ready to go; you just
              plug it in. Your whole Care Circle can join, so someone's always saying hello.
            </p>
            <div className="lp-cta-row">
              {/* Lead with the story, not a credit-card form: the hero invites a
                  scroll; the trial CTA appears after the value props (pricing +
                  final section). Signed-in users get straight to their board. */}
              {signedIn ? (
                <Link to="/home" className="lp-btn lp-btn-primary lp-btn-lg">Go to your board</Link>
              ) : (
                <>
                  <Link to="/explore" className="lp-btn lp-btn-primary lp-btn-lg">Explore Mary's Bulletin Board</Link>
                  <a href="#how" className="lp-btn lp-btn-ghost lp-btn-lg">See how it works</a>
                </>
              )}
            </div>
            {!signedIn && <p className="lp-note">Free for 30 days · Cancel anytime · The screen ships to their door</p>}
          </div>
          <div className="lp-hero-art">
            <DeviceMock />
          </div>
        </div>
      </header>

      <div className="lp-wrap">
        <div className="lp-trust">
          <div><strong>Screen included</strong><span>Ships ready to use</span></div>
          <div><strong>Nothing to learn</strong><span>No logins for your parent</span></div>
          <div><strong>Private &amp; secure</strong><span>Each Care Circle, fully separate</span></div>
          <div><strong>Cancel anytime</strong><span>Just send the screen back</span></div>
        </div>
      </div>

      <section className="lp-section" id="how">
        <div className="lp-wrap">
          <h2>How it works</h2>
          <p className="lp-lead">From sign-up to a glowing screen in the room — no tech skills, no visits required.</p>
          <div className="lp-steps">
            <div className="lp-step">
              <div className="num">1</div>
              <h3>Start your free trial</h3>
              <p>Tell us where to send the screen. We configure it for your Care Circle and ship it, ready to use.</p>
            </div>
            <div className="lp-step">
              <div className="num">2</div>
              <h3>Plug it in</h3>
              <p>Set it on a table or shelf in the room and power it on. It turns straight to your Care Circle's board — no setup, no logins, ever.</p>
            </div>
            <div className="lp-step">
              <div className="num">3</div>
              <h3>Send a little love</h3>
              <p>Post messages, photos, and reminders from your phone. They appear on the screen in seconds for your loved one to enjoy.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="lp-section">
        <div className="lp-wrap">
          <h2>Made for the whole Care Circle</h2>
          <p className="lp-lead">Effortless for everyone to post — calm and clear for your loved one to read.</p>
          <div className="lp-grid">
            <div className="lp-card"><div className="ic">💬</div><h3>Post in seconds</h3><p>A quick note from your phone — "Thinking of you, Grandma" — appears on the screen right away.</p></div>
            <div className="lp-card"><div className="ic">🖼️</div><h3>Share a photo</h3><p>Add a favorite picture to their screen. Nothing brightens a room like a familiar face.</p></div>
            <div className="lp-card"><div className="ic">⏰</div><h3>Gentle reminders</h3><p>Set a visit or event and the note glows as it approaches — a kind, timely nudge.</p></div>
            <div className="lp-card"><div className="ic">👨‍👩‍👧</div><h3>Everyone together</h3><p>Invite siblings, grandkids, and friends by email. Unlimited Care Circle members, one shared board.</p></div>
            <div className="lp-card"><div className="ic">📺</div><h3>Always on</h3><p>The included screen stays live all day — big, friendly, and readable across the room.</p></div>
            <div className="lp-card"><div className="ic">🔒</div><h3>Private &amp; secure</h3><p>Enterprise-grade Google Cloud security. Your Care Circle's space is fully isolated from every other.</p></div>
          </div>
        </div>
      </section>

      <section className="lp-section">
        <div className="lp-wrap">
          <h2>One simple price</h2>
          <p className="lp-lead">Everything included — the screen, unlimited Care Circle members, and every feature.</p>
          <div className="lp-price">
            <div className="amt">$24.95<small> / month</small></div>
            <ul>
              <li>An always-on screen, shipped ready to use</li>
              <li>Unlimited Care Circle members &amp; residents</li>
              <li>Messages, photos &amp; smart reminders</li>
              <li>30-day free trial — cancel anytime</li>
              <li>Optional cellular for rooms without Wi-Fi</li>
            </ul>
            <Link to={startHref} className="lp-btn lp-btn-primary lp-btn-lg" style={{ width: "100%" }}>{startLabel}</Link>
            <p className="lp-price-note">You won't be charged until your free trial ends.</p>
          </div>
        </div>
      </section>

      <section className="lp-section">
        <div className="lp-wrap lp-faq">
          <h2>Questions, answered</h2>
          <div className="lp-faq-grid">
            <div><h3>Do we need to buy a tablet?</h3><p>No — the screen is included in your subscription and arrives configured for your Care Circle. Just plug it in.</p></div>
            <div><h3>Does my parent have to do anything?</h3><p>Nothing at all. There are no logins or buttons — the screen simply shows your messages and photos.</p></div>
            <div><h3>What if the room has no Wi-Fi we can use?</h3><p>Add an optional cellular connection at checkout and the screen works without any facility Wi-Fi.</p></div>
            <div><h3>Can I cancel?</h3><p>Anytime. Start with a 30-day free trial; if it's not for you, cancel and send the screen back — no long-term contract.</p></div>
          </div>
        </div>
      </section>

      {!signedIn && (
        <section className="lp-final">
          <div className="lp-wrap">
            <h2>Brighten their day, starting today</h2>
            <div className="lp-cta-row">
              <Link to="/start" className="lp-btn lp-btn-primary lp-btn-lg">Start your 30-day free trial</Link>
              <Link to="/signin" className="lp-btn lp-btn-ghost lp-btn-lg">Sign in</Link>
            </div>
          </div>
        </section>
      )}

      <footer className="lp-footer">
        <div className="lp-wrap">
          © Mary's Bulletin Board · A private, secure way for families to stay close to a loved one in care.
          {" · "}
          <Link to="/terms" style={{ color: "#9db3ff" }}>Terms of Service</Link>
        </div>
      </footer>
    </div>
  );
}
